import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Camera } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import { getAuthToken } from '../services/gardenApi';
import {
  mapPlantNetMatches,
  identifyPlantFromImageFile,
  PLANT_IDENTIFY_ORGANS,
} from '../../services/plantIdentifyApi';
import { IMAGE_ACCEPT_WITH_HEIC, convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';
import { downscaleImageForPlantIdentification } from '../../utils/imageCompression';
import { useGardenPageTitle } from '../useGardenPageTitle';
import { GARDEN_IDENTIFY_PHOTO_STATE_KEY } from '../constants';

function buildNewPlantUrl({ commonName, scientificName }) {
  const q = new URLSearchParams();
  const common = (commonName || '').trim() || (scientificName || '').trim();
  const botanical = (scientificName || '').trim();
  if (common) q.set('common_name', common);
  if (botanical) q.set('botanical_name', botanical);
  return `/garden/plants/new?${q.toString()}`;
}

/** Opens AI Help with a pre-filled placement + companions prompt for this match. */
function buildAiHelpAfterIdentifyUrl({ commonName, scientificName }) {
  const q = new URLSearchParams();
  q.set('starter', 'identified');
  const c = (commonName || '').trim();
  const s = (scientificName || '').trim();
  if (c) q.set('identified_common', c);
  if (s) q.set('identified_scientific', s);
  return `/garden/ai?${q.toString()}`;
}

export default function IdentifyPlantFlowScreen() {
  useGardenPageTitle('Identify from photo', 'Identify from photo');
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [organ, setOrgan] = useState('leaf');
  const [file, setFile] = useState(null);
  const [fileLabel, setFileLabel] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const signedIn = !!getAuthToken();

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPickFile = async (e) => {
    let f = e.target.files?.[0] || null;
    e.target.value = '';
    if (!f) {
      setFile(null);
      setFileLabel('');
      return;
    }
    if (isHeicFile(f)) {
      try {
        f = await convertHeicToJpeg(f, { quality: 0.78 });
      } catch (err) {
        alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }
    try {
      f = await downscaleImageForPlantIdentification(f, 1600, 0.82);
    } catch (err) {
      console.warn('[IdentifyPlant] downscale on pick failed', err);
    }
    setFile(f);
    setFileLabel('Photo');
    setError(null);
    setResult(null);
  };

  const onSubmit = async () => {
    if (!file) {
      setError('Please choose a photo.');
      return;
    }
    if (!signedIn) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const idData = await identifyPlantFromImageFile(file, organ);
      setResult(idData);
    } catch (e) {
      console.error('[garden] identify plant:', e);
      setError(e.message || 'Failed to identify plant.');
    } finally {
      setBusy(false);
    }
  };

  const matches = result ? mapPlantNetMatches(result) : [];

  return (
    <div className="pb-24">
      <header className="flex items-center gap-2 px-2 pt-3 pb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
          Identify from photo
        </h1>
      </header>

      <div className="px-4 space-y-4 text-sm">
        <p className="text-garden-muted">
          Take or choose a clear photo (leaf or flower works best). We match it with PlantNet, then you can save the plant to your library.
        </p>

        {!signedIn ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
            <p className="font-medium">Sign in required</p>
            <p className="mt-1 text-sm opacity-90">
              Photo identification uses your account (free tier limits apply).
            </p>
            <button
              type="button"
              className="mt-2 font-semibold text-amber-900 underline"
              onClick={() => navigate('/login')}
            >
              Sign in
            </button>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-900 text-sm">
            {error}
          </div>
        ) : null}

        <div>
          <span className="block text-xs font-medium text-garden-muted mb-1">Photo</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            capture="environment"
            disabled={busy}
            onChange={onPickFile}
            className="sr-only"
            aria-label="Choose or take a plant photo"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="w-full min-h-11 inline-flex items-center justify-center gap-2 rounded-xl border border-garden-ink/15 bg-garden-primaryLight px-4 text-sm font-semibold text-garden-primaryDark active:scale-[0.99]"
          >
            <Camera size={18} aria-hidden />
            {file ? 'Change photo' : 'Choose or take photo'}
          </button>
          {file ? (
            <div className="mt-3 flex gap-3 items-center rounded-xl border border-garden-primary/30 bg-white p-3 shadow-sm">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  className="h-20 w-20 shrink-0 object-cover rounded-lg border border-black/10"
                />
              ) : (
                <div className="h-20 w-20 shrink-0 rounded-lg bg-garden-primaryLight/60 flex items-center justify-center text-[10px] text-garden-muted text-center px-1 leading-tight">
                  Image selected
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-garden-primaryDark">Photo ready</p>
                <p className="text-xs text-garden-muted truncate mt-0.5" title={fileLabel}>
                  {fileLabel}
                </p>
              </div>
            </div>
          ) : null}
          <p className="mt-2 text-[11px] text-garden-muted">
            Photos are resized on your device before upload (easier on phone memory). HEIC is supported. Max 5 MB upload.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-garden-muted mb-1">Plant part in photo</label>
          <select
            value={organ}
            onChange={(e) => setOrgan(e.target.value)}
            disabled={busy}
            className="w-full min-h-11 rounded-xl border border-garden-ink/15 px-3 bg-white text-garden-ink"
          >
            {PLANT_IDENTIFY_ORGANS.map((o) => (
              <option key={o} value={o}>
                {o.charAt(0).toUpperCase() + o.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <PrimaryButton
          type="button"
          className="w-full"
          disabled={!signedIn || !file || busy}
          onClick={onSubmit}
        >
          {busy ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Identifying…
            </>
          ) : (
            'Identify'
          )}
        </PrimaryButton>

        {matches.length > 0 ? (
          <div className="rounded-2xl border border-black/5 bg-white p-4 space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-garden-muted">
              Top matches
            </div>
            <ul className="divide-y divide-black/5">
              {matches.slice(0, 5).map((m, idx) => (
                <li key={idx} className="py-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-garden-ink truncate">
                      {m.scientificName || 'Unknown species'}
                    </div>
                    {m.commonName ? (
                      <div className="text-xs text-garden-muted truncate">{m.commonName}</div>
                    ) : null}
                    {typeof m.score === 'number' ? (
                      <div className="text-xs text-garden-primaryDark mt-1">
                        {Math.round(m.score * 100)}% match
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <button
                      type="button"
                      className="rounded-full bg-garden-primaryLight text-garden-primaryDark text-xs font-semibold px-3 py-2 min-h-9 border border-garden-primary/20"
                      onClick={() =>
                        navigate(
                          buildAiHelpAfterIdentifyUrl({
                            commonName: m.commonName || m.scientificName,
                            scientificName: m.scientificName,
                          })
                        )
                      }
                    >
                      Ask AI: where + companions
                    </button>
                    <button
                      type="button"
                      className="rounded-full bg-garden-primary text-white text-xs font-semibold px-3 py-2 min-h-9"
                      onClick={() => {
                        if (!file) return;
                        navigate(
                          buildNewPlantUrl({
                            commonName: m.commonName || m.scientificName,
                            scientificName: m.scientificName,
                          }),
                          { state: { [GARDEN_IDENTIFY_PHOTO_STATE_KEY]: file } },
                        );
                      }}
                    >
                      Use this
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-garden-muted">
              Use this keeps your photo with the plant when you save. You can edit names and details on the next screen.
            </p>
          </div>
        ) : null}

        {result && Array.isArray(result?.result?.results) && result.result.results.length === 0 ? (
          <div className="rounded-2xl border border-black/5 bg-garden-primaryLight/30 px-4 py-3 text-garden-ink">
            <p className="font-medium text-sm">No matches found</p>
            <p className="text-xs text-garden-muted mt-1">
              Try a clearer photo, different lighting, or change &ldquo;Plant part&rdquo; to match what is in the picture.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
